import { type ReactNode } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import type { Spell } from '@/schemas/spell';
import { cn } from '@/lib/utils';
import { renderInlineMarkdown } from '@/utils/inlineMarkdown';

// molecule (promosso da features/character-builder/sections/ in FI-79
// quando il bonus `<FullSheetSpells>` ha aggiunto un secondo consumer
// fuori dalla feature builder — rule of three onorata: SpellPickPanel +
// FullSheetSpells + futuri consumer Hub):
// card di un singolo incantesimo. Pattern .spell-card mockup (styles.css 1416-
// 1484): border rule + bg paper-bright + hover border-ink. Stati:
//   - selected (picked) → border 2px ink + bg paper-soft + barra sinistra 4px ink
//   - disabled         → opacity 0.45 + pointer-events-none
// Tag pill (level + state) renderizzati accanto al titolo via spellTagsForCard.
// Container `<label htmlFor>` wrap → click ovunque triggera il checkbox del
// leading slot (eredità PR 6 fix area click). Chevron `e.preventDefault()`
// per non far toggle il checkbox quando si vuole solo espandere description.
//
// **FI-79 (2026-05-23)**: description renderizzata SEMPRE inline (no più
// nascosta dietro chevron-default-closed). Pattern .spell-card__body del
// mockup originale: visible default + `line-clamp-3` + chevron per toggle
// "espandi a full / comprimi". Risolve sintomo "scelta cieca senza manuale"
// per Mago/Warlock pick (~20-30 spell pool). Cascade automatico su tutti
// i consumer (SpellPickPanel auto+pick, builder L1 + level-up flow).
//
// near-duplicate of <MoveCard>: futuro `<Card variant="move|spell">` unificato
// shared (composition principles §3.1). `<SpellCard>` resta page-specific
// finché il pattern spell-specific (tag pills + line-clamp body) divergerà
// significativamente da `<MoveCard>` molecule.

export type SpellCardTagVariant = 'lvl-0' | 'lvl-1' | 'auto' | 'pick' | 'picked' | 'state';

export interface SpellCardTag {
  label: string;
  variant: SpellCardTagVariant;
}

export interface SpellCardProps {
  spell: Spell;
  expanded: boolean;
  onToggle: () => void;
  /** Tag pill renderizzati accanto al titolo. Tipicamente: level + state. */
  tags?: readonly SpellCardTag[];
  /** Contenuto leading (checkbox shadcn). Null per spell auto-concesse. */
  leading?: ReactNode;
  /** id del checkbox/radio per cui questo card è il label (area click). */
  labelHtmlFor?: string;
  /** Se true, card "selected" con border 2px + bg paper-soft + barra sinistra. */
  selected?: boolean;
  /** Se true, card "disabled" (oltre il pick-count cap, non cliccabile). */
  disabled?: boolean;
}

const CARD_BASE_CLASS =
  'border-rule bg-paper-bright relative grid cursor-pointer grid-cols-[auto_1fr_auto] items-start gap-[0.85rem] border px-[0.95rem] py-[0.85rem] transition-[border-color,background-color] duration-150 hover:border-ink';

export function SpellCard(props: SpellCardProps) {
  const {
    spell,
    expanded,
    onToggle,
    tags = [],
    leading,
    labelHtmlFor,
    selected = false,
    disabled = false,
  } = props;

  // FI-79: description sempre renderizzata. `expanded` ora controlla solo
  // se mostrarla full o con `line-clamp-3` (compatta). Chevron toggle è
  // visibile solo se la description esiste; il suo onToggle alterna
  // clamp ↔ full (default clamp).
  const hasDescription = spell.description !== undefined && spell.description.length > 0;

  const inner = (
    <>
      <LeadingSlot>{leading}</LeadingSlot>
      <SpellHead name={spell.name} tags={tags} />
      {hasDescription ? (
        <ExpandToggle expanded={expanded} onToggle={onToggle} />
      ) : (
        <span aria-hidden />
      )}
      <SpellBody description={spell.description} expanded={expanded} />
    </>
  );

  return (
    <CardShell labelHtmlFor={labelHtmlFor} selected={selected} disabled={disabled}>
      {inner}
    </CardShell>
  );
}

function CardShell({
  labelHtmlFor,
  selected,
  disabled,
  children,
}: {
  labelHtmlFor: string | undefined;
  selected: boolean;
  disabled: boolean;
  children: ReactNode;
}) {
  const className = cn(
    CARD_BASE_CLASS,
    selected && 'border-ink border-2 bg-paper-soft pl-[calc(0.95rem-1px)]',
    disabled && 'pointer-events-none opacity-45',
    !labelHtmlFor && 'cursor-default',
  );
  if (labelHtmlFor) {
    return (
      <label htmlFor={labelHtmlFor} data-selected={selected || undefined} className={className}>
        {selected ? <SelectedBar /> : null}
        {children}
      </label>
    );
  }
  return (
    <div data-selected={selected || undefined} className={className}>
      {selected ? <SelectedBar /> : null}
      {children}
    </div>
  );
}

function SelectedBar() {
  return (
    <span aria-hidden className="bg-ink pointer-events-none absolute top-0 bottom-0 left-0 w-1" />
  );
}

function SpellHead({ name, tags }: { name: string; tags: readonly SpellCardTag[] }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
      <span className="font-display text-[1.02rem] leading-[1.15] font-bold tracking-[0.04em] uppercase">
        {name}
      </span>
      {tags.length > 0 ? (
        <span className="inline-flex flex-wrap items-baseline gap-[0.32rem]">
          {tags.map((tag, i) => (
            <SpellTag key={`${tag.label}-${i}`} tag={tag} />
          ))}
        </span>
      ) : null}
    </div>
  );
}

function SpellTag({ tag }: { tag: SpellCardTag }) {
  return (
    <span
      className={cn(
        'whitespace-nowrap border px-[0.55em] py-[0.18em] text-[0.62rem] tracking-[0.08em] uppercase',
        'font-display',
        tagVariantClasses(tag.variant),
      )}
    >
      {tag.label}
    </span>
  );
}

function tagVariantClasses(variant: SpellCardTagVariant): string {
  if (variant === 'lvl-0') return 'border-ink bg-ink text-paper-bright';
  if (variant === 'lvl-1') return 'border-ink bg-paper-soft text-ink';
  if (variant === 'auto') return 'border-ink bg-ink text-paper-bright';
  if (variant === 'picked') return 'border-ink bg-ink text-paper-bright';
  if (variant === 'pick') return 'border-ink text-ink bg-transparent';
  // state (es. "continuato"): font-serif italic, no uppercase
  return 'border-transparent font-serif text-ink-soft text-[0.72rem] tracking-normal normal-case italic';
}

/**
 * FI-79: body description renderizzato SEMPRE quando presente.
 * `expanded=false` (default) → `line-clamp-3` (compatta, ~3 righe leggibili).
 * `expanded=true` → testo full, no clamp.
 * Nessuna description → render `null` (no spazio occupato).
 */
function SpellBody({
  description,
  expanded,
}: {
  description: string | undefined;
  expanded: boolean;
}) {
  if (!description) return null;
  return (
    <div className="col-span-3">
      <SpellDescription text={description} expanded={expanded} />
    </div>
  );
}

function LeadingSlot({ children }: { children: ReactNode }) {
  if (!children) return <span aria-hidden />;
  return <div className="mt-[0.15em] flex-shrink-0">{children}</div>;
}

function ExpandToggle({ expanded, onToggle }: { expanded: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      onClick={(e) => {
        // preventDefault: evita che il <label> wrapping triggeri il checkbox.
        // stopPropagation: evita bubble verso il container.
        e.preventDefault();
        e.stopPropagation();
        onToggle();
      }}
      aria-label={expanded ? 'Comprimi descrizione' : 'Mostra descrizione completa'}
      className="text-ink-soft hover:text-ink grid h-7 w-7 shrink-0 cursor-pointer place-items-center bg-transparent transition-colors"
    >
      {expanded ? (
        <ChevronUp aria-hidden className="h-4 w-4" strokeWidth={2.2} />
      ) : (
        <ChevronDown aria-hidden className="h-4 w-4" strokeWidth={2.2} />
      )}
    </button>
  );
}

/**
 * FI-79: description visibile inline. `expanded=true` → testo full;
 * `expanded=false` → `line-clamp-3` (≈3 righe leggibili compatte). Stile
 * coerente con `<MoveCard>::ExpandedDescription` (font-serif, border-l
 * accent, padding interno).
 */
function SpellDescription({ text, expanded }: { text: string; expanded: boolean }) {
  return (
    <div
      className={cn(
        'border-rule font-serif text-ink-soft mt-2 border-l-2 pl-[0.85rem] text-[0.92rem] leading-relaxed whitespace-pre-line',
        !expanded && 'line-clamp-3',
      )}
    >
      {renderInlineMarkdown(text)}
    </div>
  );
}

// spellTagsForCard helper vive in `spellCardHelpers.ts` per rispettare
// `react-refresh/only-export-components` (questo file esporta solo SpellCard).
